/* ──────────────────────────────────────────────────────────
 *  ChatStateService
 *
 *  Centralised reactive state for the chat widget.
 *  Uses Angular signals for fine-grained reactivity.
 * ────────────────────────────────────────────────────────── */

import {
  Injectable,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, switchMap, catchError, EMPTY } from 'rxjs';

import {
  ChatUser,
  ChatThread,
  ChatMessage,
  TypingIndicatorEvent,
} from '../models';
import { ChatApiService } from './chat-api.service';

@Injectable({ providedIn: 'root' })
export class ChatStateService {
  private readonly api = inject(ChatApiService);
  private readonly destroyRef = inject(DestroyRef);

  /* ── Signals ─────────────────────────────────────────── */

  /** The currently "logged-in" user (chosen from the seed users). */
  readonly currentUser = signal<ChatUser | null>(null);

  /** All users in the system. */
  readonly users = signal<ChatUser[]>([]);

  /** Thread list for the current user. */
  readonly threads = signal<ChatThread[]>([]);

  /** Currently selected thread. */
  readonly activeThreadId = signal<string | null>(null);

  /** Messages for the active thread. */
  readonly messages = signal<ChatMessage[]>([]);

  /** Who is typing in the active thread right now. */
  readonly typingIndicators = signal<TypingIndicatorEvent[]>([]);

  /** Derived: the active thread object. */
  readonly activeThread = computed(() => {
    const id = this.activeThreadId();
    return this.threads().find((t) => t.threadId === id) ?? null;
  });

  /** Total unread badge count across all threads. */
  readonly totalUnread = computed(() =>
    this.threads().reduce((sum, t) => sum + t.unreadCount, 0),
  );

  /* ── Bootstrap ───────────────────────────────────────── */

  /** Call once on startup after a user is selected. */
  init(user: ChatUser): void {
    this.currentUser.set(user);
    this.loadThreads();
    this.startTypingPolling();
  }

  loadUsers(): void {
    this.api
      .getUsers()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          console.error('[ChatState] Failed to load users', err);
          return EMPTY;
        }),
      )
      .subscribe((u) => this.users.set(u));
  }

  loadThreads(): void {
    const uid = this.currentUser()?.id;
    if (!uid) return;
    this.api
      .getThreads(uid)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((err) => {
          console.error('[ChatState] Failed to load threads', err);
          return EMPTY;
        }),
      )
      .subscribe((t) => this.threads.set(t));
  }

  /* ── Thread / message actions ────────────────────────── */

  selectThread(threadId: string): void {
    this.activeThreadId.set(threadId);
    this.loadMessages(threadId);
  }

  loadMessages(threadId: string): void {
    this.api
      .getMessages(threadId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((m) => this.messages.set(m));
  }

  sendMessage(content: string): void {
    const threadId = this.activeThreadId();
    const user = this.currentUser();
    if (!threadId || !user) return;

    // Optimistic local insert
    const tempMsg: ChatMessage = {
      id: `temp-${Date.now()}`,
      threadId,
      senderId: user.id,
      senderDisplayName: user.displayName,
      content,
      createdOn: new Date().toISOString(),
      status: 'sending',
      type: 'text',
    };
    this.messages.update((msgs) => [...msgs, tempMsg]);

    this.api
      .sendMessage(threadId, user.id, content)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          // Replace the optimistic msg with the server response
          this.messages.update((msgs) =>
            msgs.map((m) => (m.id === tempMsg.id ? saved : m)),
          );
          this.loadThreads(); // refresh last-message + ordering
        },
        error: () => {
          this.messages.update((msgs) =>
            msgs.map((m) =>
              m.id === tempMsg.id ? { ...m, status: 'failed' as const } : m,
            ),
          );
        },
      });
  }

  sendTyping(): void {
    const threadId = this.activeThreadId();
    const user = this.currentUser();
    if (!threadId || !user) return;
    this.api
      .sendTypingIndicator(threadId, user.id, user.displayName)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  createThread(participantIds: string[], topic: string): void {
    const user = this.currentUser();
    if (!user) return;
    const allIds = [user.id, ...participantIds.filter((id) => id !== user.id)];

    this.api
      .createThread({ topic, participantIds: allIds, retentionPolicy: 'indefinite' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.loadThreads();
        this.selectThread(res.threadId);
      });
  }

  /* ── Polling ─────────────────────────────────────────── */

  private startTypingPolling(): void {
    interval(2000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          const tid = this.activeThreadId();
          const uid = this.currentUser()?.id;
          if (!tid || !uid) return EMPTY;
          return this.api.getTypingIndicators(tid, uid);
        }),
        catchError(() => EMPTY),
      )
      .subscribe((indicators) => this.typingIndicators.set(indicators));

    // Also poll messages every 3 s for the active thread
    interval(3000)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(() => {
          const tid = this.activeThreadId();
          if (!tid) return EMPTY;
          return this.api.getMessages(tid);
        }),
        catchError(() => EMPTY),
      )
      .subscribe((m) => this.messages.set(m));
  }
}
